import React, { useRef, Suspense } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { Button, Divider, Header, Icon, Segment } from "semantic-ui-react";
import { useInitialNoteData } from "../../data/notes/useInitialNoteData";

import { ControlledObservationFormFields, IAuditNote, IAuditNoteFormData, TEditableAuditNote } from "../../interfaces/IAuditNote";
import IFormType from "../../interfaces/IFormType";
import { EditableAuditNoteForm } from "./EditableAuditNoteForm";

import "./_AuditNoteModal.less";
import { useEditableNotes } from "../../data/notes/useEditableNotes";
import { NoteID } from "../../utils/kierrokset.helper";
import { IFormSchema } from "../../interfaces/IFormSchema";
import { useObservationContext } from "../../data/observations/useObservationContext";
import IObservation from "../../interfaces/IObservation";

const getTextProps = (t: TFunction) => ({
    save: t("Tallenna"),
    cancel: t("Peruuta"),
    addNote: t("Lisää huomio"),
});

interface EditableAuditNoteModalProps {
    // fieldKey: IAuditNote["key"];
    parentObjectTitle?: string;
    fieldTitle: string;
    auditFormType: IFormType["id"];
    observationFormType?: string;

    onSubmit?: (auditNote: TEditableAuditNote) => void;
    onCancel?: () => void;

    initialData: IAuditNoteFormData;
    initialNote?: TEditableAuditNote;
};

export const EditableAuditNoteModal: React.FC<EditableAuditNoteModalProps> = ({
    fieldTitle,
    parentObjectTitle,
    observationFormType,
    auditFormType,
    onSubmit,
    onCancel,

    initialData,
    initialNote,

}) => {
    const texts = getTextProps(useTranslation().t);
    const submitButton = useRef<HTMLButtonElement>(null);


    const cancel = () => onCancel && onCancel();
    const submit = () => submitButton.current && submitButton.current.click()

    const { data: contextData } = useObservationContext()
    const observationFormTypeId = contextData.formTypes.find((form) => observationFormType === form.referenceName)?.id;


    const handleSubmit = (formData: IAuditNoteFormData, observationSchema?: IFormSchema) => {
        if (!initialData.key && !initialNote?.key) {
            console.error("Failed to submit, missing valid key");
            return;
        }

        if (initialNote) {
            onSubmit && onSubmit({
                id: initialNote?.id,
                key: initialNote.key,
                submitStatus: initialNote.submitStatus || "unsubmitted",
                unsubmitted: (initialNote.submitStatus === "submitted") ? false as any : true,
                //@ts-ignore
                observationSchema,
                formType: observationFormTypeId,
                formData,

                title: formData[ControlledObservationFormFields.title] || "",
                details: formData[ControlledObservationFormFields.details] || "",
                images: formData[ControlledObservationFormFields.images],
            });
        } else {
            onSubmit && onSubmit({
                id: NoteID(),
                key: initialData.key as any,
                submitStatus: "unsubmitted",
                unsubmitted: true,
                //@ts-ignore
                observationSchema,
                formType: observationFormTypeId,
                formData,
            });
        }
    };


    const relatedObservation = initialNote?.submitStatus === "submitted" ? initialNote?.relatedObservation : (initialNote as any)?.relatedObservation;

    const modal = (
        <div className="create-note-modal-wrapper">
            <div className="create-note-modal">
                <Segment textAlign="left" padded>
                    <Suspense fallback={<Segment basic loading placeholder />}>

                        <Header as="h2"> {texts.addNote} </Header>
                        <span className="create-note-modal-close" onClick={cancel}>
                            <Icon name="times" />
                        </span>

                        <div className="create-note-modal-field-title">
                            <strong> {parentObjectTitle ? `${parentObjectTitle} / ` : null} {fieldTitle} </strong>
                        </div>


                        <EditableAuditNoteForm
                            observationFormType={observationFormType}
                            auditFormType={auditFormType}
                            initialFormData={initialData}
                            onSubmit={handleSubmit}
                            submitButton={submitButton}
                            relatedObservation={relatedObservation || null}
                        />

                        <Divider hidden />
                        <div style={{ display: "flex" }}>
                            <Button type="button" size="large" content={texts.save} onClick={submit} icon="save" primary fluid />
                            <Button type="button" size="large" content={texts.cancel} onClick={cancel} fluid />
                        </div>
                    </Suspense>
                </Segment>
            </div>
        </div>

    );



    const parent = document.getElementById("create-note-modal-container");
    if (!parent) {
        return null
    }

    return ReactDOM.createPortal(modal, parent);
};

export default EditableAuditNoteModal;